"use client";

// DelegationPanel — "hire an AI agent to run a department" loop, made real.
//
// Pure / presentational + a single fetch to /api/delegate/run. The player picks
// one of their party Surggies, picks a task that fits that Surggie's type
// (tech → code, finance → market signals, everything-creative → social posts),
// fills in the brief, and dispatches it. The specialist tool runs server-side
// (falling back to a labelled mock when its backend isn't configured), the
// result is shown inline, and the Surggie earns XP.
//
// All party data arrives via props; XP is awarded back through `onComplete`.
// The PARENT owns the party + applies XP via applyXp/updatePartyMember — this
// component never imports the game store. SSR-safe: no top-level window access,
// renders nothing until `open`.
//
// Palette: strict cream + ink + gold accent (PIXEL_STYLE.md), matching
// InventoryScreen / SurggieCenter chrome.

import { useCallback, useEffect, useMemo, useState } from "react";
import type { CSSProperties } from "react";

import { PixelText } from "@/components/ui/pixel-text";
import { resolveSpecies } from "@/engine/battle-participant";
import { track, ANALYTICS_EVENTS } from "@/lib/analytics";

// ── Palette tokens (mirrors PIXEL_STYLE.md / sibling panels) ────────────────
const CREAM = "#f5ecd6";
const INK = "#1a1a1a";
const GOLD = "#c5a83a";
const WALNUT = "#5c4a30";
const RUNNING = "#c5a83a";
const DONE = "#4a9c3e";

// XP awarded per completed delegation. Mirrors the modest, level-driven curve in
// engine/party (xpToNext(1) = 35), so a delegation is a meaningful but not
// trivial chunk of a low-level Surggie's next level.
const DELEGATION_XP = 24;

// ── Public API ──────────────────────────────────────────────────────────────

export type DelegationPanelMember = {
  /** Species sprite key, e.g. "surggie_3". */
  speciesId: string;
  nickname?: string;
  level: number;
};

export type DelegationPanelProps = {
  open: boolean;
  /** Party to delegate from (read-only). Index is the party slot. */
  party: DelegationPanelMember[];
  /**
   * Called once a delegation completes. The PARENT awards XP via
   * applyXp(party[partyIndex], xp) → updatePartyMember(partyIndex, …).
   */
  onComplete: (partyIndex: number, xpAwarded: number) => void;
  onClose: () => void;
};

// ── Task catalog ────────────────────────────────────────────────────────────
// Each task maps to a real specialist tool + the field the player fills in.

type TaskDef = {
  toolName: "code_with_huskii" | "get_market_signals" | "generate_creatus_post";
  /** Verb shown on the dispatch button + result header. */
  title: string;
  /** One-line pitch for the task. */
  blurb: string;
  /** The tool param the free-text input feeds. */
  inputKey: string;
  inputLabel: string;
  placeholder: string;
  /** Build the tool params from the brief. */
  buildParams: (brief: string) => Record<string, unknown>;
};

const CODE_TASK: TaskDef = {
  toolName: "code_with_huskii",
  title: "Ship Code",
  blurb: "Spin up the Huskii coding agent to build or fix something.",
  inputKey: "task",
  inputLabel: "Coding task",
  placeholder: "e.g. add a /health endpoint to the API",
  buildParams: (brief) => ({ task: brief }),
};

const SIGNALS_TASK: TaskDef = {
  toolName: "get_market_signals",
  title: "Pull Market Signals",
  blurb: "Fetch the latest FSB trading signals from the markets desk.",
  inputKey: "symbol",
  inputLabel: "Symbol (optional)",
  placeholder: "e.g. EURUSD — leave blank for all",
  buildParams: (brief) => (brief.trim() ? { symbol: brief.trim(), limit: 5 } : { limit: 5 }),
};

const CONTENT_TASK: TaskDef = {
  toolName: "generate_creatus_post",
  title: "Draft Social Posts",
  blurb: "Turn an idea or update into platform-tailored social posts.",
  inputKey: "source",
  inputLabel: "Source / idea",
  placeholder: "Paste a transcript, update, or idea to repurpose",
  buildParams: (brief) => ({ source: brief, tone: "punchy" }),
};

/** Pick the task that best fits a Surggie's species type. */
function taskForSpecies(speciesId: string): TaskDef {
  const type = resolveSpecies(speciesId)?.type1;
  switch (type) {
    case "tech":
      return CODE_TASK;
    case "finance":
      return SIGNALS_TASK;
    default:
      // sales / creative / hr / ops all create content well.
      return CONTENT_TASK;
  }
}

function speciesName(speciesId: string): string {
  return resolveSpecies(speciesId)?.name ?? speciesId;
}

// ── Result rendering ────────────────────────────────────────────────────────

type RunStatus = "idle" | "running" | "completed" | "error";

type RunResponse = {
  status: string;
  toolName: string;
  mock: boolean;
  result: unknown;
  realError?: string | null;
};

/** Compact, human-readable view of an arbitrary tool result. */
function ResultBody({ data }: { data: unknown }): React.ReactElement {
  const r = (data ?? {}) as Record<string, unknown>;

  // generate_creatus_post → posts[]
  if (Array.isArray(r.posts)) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {(r.posts as Array<Record<string, unknown>>).map((p, i) => (
          <div key={i} style={resultCardStyle}>
            <div style={{ color: GOLD, fontSize: "0.7rem", textTransform: "uppercase" }}>
              {String(p.platform ?? "post")}
            </div>
            <div style={{ color: INK, whiteSpace: "pre-wrap" }}>{String(p.text ?? "")}</div>
          </div>
        ))}
      </div>
    );
  }

  // get_market_signals → signals[]
  if (Array.isArray(r.signals)) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {(r.signals as Array<Record<string, unknown>>).slice(0, 6).map((s, i) => (
          <div key={i} style={{ ...resultCardStyle, display: "flex", gap: 8, flexWrap: "wrap" }}>
            <span style={{ color: INK, fontWeight: "bold" }}>{String(s.symbol ?? "")}</span>
            <span style={{ color: WALNUT }}>{String(s.signalType ?? "")}</span>
            <span style={{ color: WALNUT }}>{String(s.timeframe ?? "")}</span>
            <span style={{ color: WALNUT }}>@ {String(s.levelPrice ?? "")}</span>
          </div>
        ))}
      </div>
    );
  }

  // code_with_huskii → summary / steps / stdout
  if (r.summary || Array.isArray(r.steps) || r.stdout) {
    return (
      <div style={resultCardStyle}>
        {r.summary ? (
          <div style={{ color: INK, marginBottom: 6 }}>{String(r.summary)}</div>
        ) : null}
        {Array.isArray(r.steps) ? (
          <ul style={{ margin: 0, paddingLeft: "1.1rem", color: WALNUT }}>
            {(r.steps as unknown[]).map((s, i) => (
              <li key={i}>{String(s)}</li>
            ))}
          </ul>
        ) : null}
        {r.stdout ? (
          <pre style={{ margin: "6px 0 0", color: WALNUT, whiteSpace: "pre-wrap", fontSize: "0.7rem" }}>
            {String(r.stdout).slice(0, 1200)}
          </pre>
        ) : null}
      </div>
    );
  }

  // Fallback: pretty JSON.
  return (
    <pre style={{ ...resultCardStyle, whiteSpace: "pre-wrap", color: WALNUT, fontSize: "0.7rem" }}>
      {JSON.stringify(r, null, 2).slice(0, 1500)}
    </pre>
  );
}

const resultCardStyle: CSSProperties = {
  backgroundColor: "#efe4c8",
  border: `2px solid ${WALNUT}`,
  padding: "8px 10px",
  fontFamily: "monospace",
  fontSize: "0.8rem",
};

// ── Component ───────────────────────────────────────────────────────────────

export function DelegationPanel(props: DelegationPanelProps): React.ReactElement | null {
  const { open, party, onComplete, onClose } = props;

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [brief, setBrief] = useState("");
  const [status, setStatus] = useState<RunStatus>("idle");
  const [response, setResponse] = useState<RunResponse | null>(null);
  const [errorText, setErrorText] = useState<string | null>(null);

  const selected = party[selectedIndex];
  const task = useMemo(
    () => (selected ? taskForSpecies(selected.speciesId) : null),
    [selected],
  );

  // Reset transient state when the panel opens, or when the selection changes.
  useEffect(() => {
    if (!open) return;
    setStatus("idle");
    setResponse(null);
    setErrorText(null);
    setBrief("");
  }, [open, selectedIndex]);

  // Esc closes (only while not mid-dispatch).
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape" && status !== "running") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, status, onClose]);

  const dispatch = useCallback(async () => {
    if (!task || !selected) return;
    setStatus("running");
    setResponse(null);
    setErrorText(null);
    try {
      const res = await fetch("/api/delegate/run", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ toolName: task.toolName, params: task.buildParams(brief) }),
      });
      if (!res.ok) {
        const j = (await res.json().catch(() => null)) as { error?: string } | null;
        setErrorText(j?.error ?? `Request failed (${res.status})`);
        setStatus("error");
        return;
      }
      const data = (await res.json()) as RunResponse;
      setResponse(data);
      setStatus("completed");
      track(ANALYTICS_EVENTS.DELEGATION_RUN, { tool: task.toolName });
      // Award XP through the parent's party XP path.
      onComplete(selectedIndex, DELEGATION_XP);
    } catch (err) {
      setErrorText((err as Error).message);
      setStatus("error");
    }
  }, [task, selected, brief, selectedIndex, onComplete]);

  if (!open) return null;

  const canDispatch =
    status !== "running" &&
    !!task &&
    // get_market_signals tolerates an empty brief; the others want one.
    (task.toolName === "get_market_signals" || brief.trim().length > 0);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Delegate a task"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 80,
        backgroundColor: "rgba(26, 26, 26, 0.78)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        imageRendering: "pixelated",
        padding: "1.25rem",
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && status !== "running") onClose();
      }}
    >
      <div
        style={{
          width: "min(54rem, 100%)",
          maxHeight: "100%",
          backgroundColor: CREAM,
          border: `2px solid ${INK}`,
          padding: "0.875rem",
          display: "flex",
          flexDirection: "column",
          gap: "0.75rem",
          overflow: "auto",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: `2px solid ${INK}`,
            paddingBottom: "0.5rem",
          }}
        >
          <PixelText as="div" glow="none" size="lg" className="!text-[#1a1a1a]">
            DELEGATE
          </PixelText>
          <button
            type="button"
            aria-label="Close delegation panel"
            onClick={() => onClose()}
            disabled={status === "running"}
            className="font-mono font-bold uppercase tracking-widest"
            style={{
              backgroundColor: CREAM,
              color: INK,
              border: `2px solid ${INK}`,
              padding: "0.25rem 0.625rem",
              fontSize: "0.85rem",
              lineHeight: 1,
              cursor: status === "running" ? "not-allowed" : "pointer",
              opacity: status === "running" ? 0.5 : 1,
              imageRendering: "pixelated",
            }}
          >
            X
          </button>
        </div>

        {party.length === 0 ? (
          <div style={{ color: WALNUT, fontFamily: "monospace", padding: "1rem 0" }}>
            No Surggies in your party yet. Recruit one, then assign it a job.
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "16rem 1fr", gap: "0.75rem" }}>
            {/* Left: party roster */}
            <div
              role="listbox"
              aria-label="Party"
              style={{ display: "flex", flexDirection: "column", gap: 4 }}
            >
              {party.map((m, i) => {
                const active = i === selectedIndex;
                return (
                  <button
                    key={i}
                    type="button"
                    role="option"
                    aria-selected={active}
                    onClick={() => setSelectedIndex(i)}
                    className="font-mono"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      textAlign: "left",
                      backgroundColor: active ? GOLD : CREAM,
                      color: INK,
                      border: `2px solid ${INK}`,
                      padding: "6px 8px",
                      cursor: "pointer",
                      imageRendering: "pixelated",
                    }}
                  >
                    <span style={{ fontWeight: "bold" }}>
                      {m.nickname?.trim() || speciesName(m.speciesId)}
                    </span>
                    <span style={{ color: WALNUT, fontSize: "0.75rem" }}>Lv {m.level}</span>
                  </button>
                );
              })}
            </div>

            {/* Right: task + dispatch + result */}
            <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem", minWidth: 0 }}>
              {task && selected && (
                <>
                  <div style={{ fontFamily: "monospace", color: INK }}>
                    <span style={{ color: GOLD, fontWeight: "bold" }}>
                      {selected.nickname?.trim() || speciesName(selected.speciesId)}
                    </span>{" "}
                    is best at:{" "}
                    <span style={{ fontWeight: "bold" }}>{task.title}</span>
                  </div>
                  <div style={{ fontFamily: "monospace", color: WALNUT, fontSize: "0.8rem" }}>
                    {task.blurb}
                  </div>

                  <label
                    style={{
                      fontFamily: "monospace",
                      color: INK,
                      fontSize: "0.8rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                    }}
                  >
                    {task.inputLabel}
                  </label>
                  <textarea
                    value={brief}
                    onChange={(e) => setBrief(e.target.value)}
                    placeholder={task.placeholder}
                    rows={3}
                    disabled={status === "running"}
                    style={{
                      backgroundColor: "#efe4c8",
                      color: INK,
                      border: `2px solid ${INK}`,
                      padding: "8px",
                      fontFamily: "monospace",
                      fontSize: "0.85rem",
                      resize: "vertical",
                    }}
                  />

                  <button
                    type="button"
                    onClick={dispatch}
                    disabled={!canDispatch}
                    className="font-mono font-bold uppercase tracking-widest"
                    style={{
                      alignSelf: "flex-start",
                      backgroundColor: canDispatch ? INK : "#a89c7c",
                      color: CREAM,
                      border: `2px solid ${INK}`,
                      padding: "8px 16px",
                      cursor: canDispatch ? "pointer" : "not-allowed",
                      imageRendering: "pixelated",
                    }}
                  >
                    {status === "running" ? "Working…" : "Dispatch"}
                  </button>

                  {/* Status line */}
                  {status === "running" && (
                    <div style={{ fontFamily: "monospace", color: RUNNING, fontSize: "0.8rem" }}>
                      ▸ {selected.nickname?.trim() || speciesName(selected.speciesId)} is on it…
                    </div>
                  )}
                  {status === "error" && (
                    <div style={{ fontFamily: "monospace", color: "#b04848", fontSize: "0.8rem" }}>
                      Delegation failed: {errorText}
                    </div>
                  )}
                  {status === "completed" && response && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      <div
                        style={{
                          fontFamily: "monospace",
                          color: DONE,
                          fontWeight: "bold",
                          fontSize: "0.85rem",
                        }}
                      >
                        ✓ Complete — +{DELEGATION_XP} XP
                        {response.mock ? "  (simulated — backend not configured)" : ""}
                      </div>
                      <ResultBody data={response.result} />
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default DelegationPanel;
